/* Dealerships (the org tree) and Access (A1), super-admin views and the dealer view. */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, SegmentedControl } from '../ui';
import {
    countAll, flattenOrgs, ROLE_LABEL, signInPath, TYPE_LABEL, USER_KIND_LABEL, userKind,
} from '../../utils/adminConsole';
import { accessOf } from '../../utils/adminAccess';
import {
    AccessCell, Alert, Avatar, OrgTypeChip, SectionHead, SourceChip, StatusChip,
} from './bits';
import { LockIcon, PencilIcon, PlusIcon } from './icons';

// ── Dealerships (P-ADM-028..044, 102..109) ─────────────────────────────
export function DealershipsSection({
    c, users, tenants, onTeam, onOnboard,
}) {
    const { isSuper, orgs } = c;
    const rows = flattenOrgs(orgs);
    const tenantOf = new Map((tenants || []).map((t) => [t.org_id, t]));
    const membersOf = (id) => users.filter((u) => u.org_id === id).length;

    return (
        <>
            <SectionHead
                eyebrow={isSuper ? 'Platform' : 'Your dealership'}
                title={isSuper ? 'Dealerships' : 'Dealership'}
                actions={isSuper ? <Button variant="primary" onClick={onOnboard}><PlusIcon />Onboard dealership</Button> : null}
            >
                {isSuper
                    ? 'Every organization on IronSight: Fusable, holding groups and the dealerships beneath them, with each dealership’s data source.'
                    : 'Your workspace and the address your team signs in at.'}
            </SectionHead>

            <Alert>{c.actionError || c.error}</Alert>

            {isSuper && !c.loading && (
                <div className="ad2-stats ad2-stats--static" role="group" aria-label="Totals">
                    {[
                        { l: 'Holdings', n: countAll(orgs, 'TOG') },
                        { l: 'Dealerships', n: countAll(orgs, 'COG') },
                        { l: 'Users', n: users.length },
                    ].map((k) => (
                        <div key={k.l} className="ad2-stat">
                            <span className="ad2-stat-n">{k.n}</span>
                            <span className="ad2-stat-l">{k.l}</span>
                        </div>
                    ))}
                </div>
            )}

            <div className="v2-board ad2-board">
                <div className="v2-tscroll">
                    <table className="v2-table ad2-orgs" aria-label="Organizations">
                        <thead>
                            <tr>
                                <th scope="col"><span>Organization</span></th>
                                <th scope="col"><span>Sign-in address</span></th>
                                <th scope="col"><span>Status</span></th>
                                <th scope="col" className="v2-r"><span>Users</span></th>
                                {isSuper && <th scope="col"><span>Data source</span></th>}
                                <th scope="col"><span className="v2-sr">Actions</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((o) => (
                                <tr key={o.id} className={o.status === 'suspended' ? 'ad2-dim' : undefined}>
                                    <td>
                                        <div className="ad2-tree-n">
                                            {o.depth > 0 && <span className="ad2-ind" style={{ marginLeft: `${(o.depth - 1) * 16 + 6}px` }} aria-hidden="true" />}
                                            <b>{o.name}</b>
                                            <OrgTypeChip type={o.type} label={TYPE_LABEL[o.type] || o.type} />
                                        </div>
                                    </td>
                                    <td><span className="ad2-slug">{signInPath(o)}</span></td>
                                    <td><StatusChip status={o.status} /></td>
                                    <td className="v2-r ad2-num">{membersOf(o.id)}</td>
                                    {isSuper && (
                                        <td>{o.type === 'COG' ? <SourceChip tenant={tenantOf.get(o.id)} /> : <span className="ad2-muted">—</span>}</td>
                                    )}
                                    <td>
                                        <span className="ad2-row-acts">
                                            {isSuper && o.type === 'TOG' && (
                                                <Button size="sm" onClick={() => c.openAddChild(o.node)}><PlusIcon />Add dealership</Button>
                                            )}
                                            <Button size="sm" aria-label={`Team of ${o.name}`} onClick={() => onTeam(o)}>Team</Button>
                                            {/* D20: Edit stays hidden for dealer admins. */}
                                            {isSuper && <Button size="sm" aria-label={`Edit ${o.name}`} onClick={() => c.setEditTarget(o.node)}>Edit</Button>}
                                            {isSuper && (
                                                <Button size="sm" className="ad2-btn-danger" aria-label={`Delete ${o.name}`} onClick={() => c.openDelete(o.node)}>Delete</Button>
                                            )}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {!c.loading && rows.length === 0 && <div className="ad2-empty"><p>No organizations yet.</p></div>}
                </div>
                {isSuper && (
                <div className="ad2-foot">
                    Delete only works on an empty organization: move or remove its users and sub-organizations, and detach its
                    data source, first. Suspending cascades to everything beneath.
                </div>
                )}
            </div>
        </>
    );
}
DealershipsSection.propTypes = {
    c: PropTypes.object.isRequired, users: PropTypes.array.isRequired, tenants: PropTypes.array, onTeam: PropTypes.func.isRequired, onOnboard: PropTypes.func.isRequired,
};

// ── Access (A1, super-admin only, D19) ─────────────────────────────────
export function AccessSection({
    c, users, tenants, onOpen,
}) {
    const [who, setWho] = useState('all');
    const dealers = flattenOrgs(c.orgs).filter((o) => o.type === 'COG');
    const list = users.filter((u) => {
        const kind = userKind(u, c.orgs);
        if (who === 'staff') return kind === 'staff';
        if (who === 'dealer') return kind !== 'staff';
        return true;
    });

    return (
        <>
            <SectionHead eyebrow="Platform" title="Access">
                Who can do what, in which dealership. Nobody sets these cells by hand: they follow from three rules.
            </SectionHead>
            <div className="ad2-rules">
                <div className="ad2-rule">
                    <h3><span className="ad2-acc ad2-acc--edit"><PencilIcon />Edit</span></h3>
                    <p><b>Member of the dealership.</b> Dealer users and dealer admins whose home organization is that dealership. They can create tasks, notes and changes.</p>
                </div>
                <div className="ad2-rule">
                    <h3><span className="ad2-acc ad2-acc--ro"><LockIcon />Read-only</span></h3>
                    <p><b>Fusable Super Admin with no dealership membership.</b> Sees every connected dealership but cannot write, so a staff member cannot create tasks in a dealer’s list.</p>
                </div>
                <div className="ad2-rule">
                    <h3><span className="ad2-acc ad2-acc--none">None</span></h3>
                    <p><b>Everyone else.</b> Deactivated, IronSight off, Fusable staff without Super Admin, and holding-level members (holdings do not reach dealership data today).</p>
                </div>
            </div>
            <div className="v2-board ad2-board">
                <div className="ad2-ctl">
                    <SegmentedControl
                        label="Who to show"
                        value={who}
                        onChange={setWho}
                        options={[{ value: 'all', label: 'Everyone' }, { value: 'staff', label: 'Fusable staff' }, { value: 'dealer', label: 'Dealer users' }]}
                    />
                    <span className="ad2-spacer" />
                    <span className="ad2-count"><b>{list.length}</b> users × {dealers.length} dealerships</span>
                </div>
                <div className="v2-tscroll">
                    <table className="v2-table ad2-amx" aria-label="Access by user and dealership">
                        <thead>
                            <tr>
                                <th scope="col"><span>User</span></th>
                                {dealers.map((d) => <th key={d.id} scope="col" className="ad2-dh"><span>{d.name}</span></th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((u) => {
                                const a = accessOf(u, c.orgs, tenants);
                                const kind = userKind(u, c.orgs);
                                return (
                                    <tr
                                        key={u.id}
                                        tabIndex={0}
                                        className={`v2-row-action${u.status === 'suspended' ? ' ad2-dim' : ''}`}
                                        onClick={() => onOpen(u)}
                                        onKeyDown={(e) => {
                                            if (e.target !== e.currentTarget) return;
                                            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(u); }
                                            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                                                e.preventDefault();
                                                (e.key === 'ArrowDown' ? e.currentTarget.nextElementSibling : e.currentTarget.previousElementSibling)?.focus();
                                            }
                                        }}
                                    >
                                        <td>
                                            <div className="ad2-who">
                                                <Avatar user={u} kind={kind} />
                                                <div>
                                                    <div className="ad2-nm">{u.display_name}</div>
                                                    <div className="ad2-em">{USER_KIND_LABEL[kind]} · {ROLE_LABEL[u.role] || u.role}</div>
                                                </div>
                                            </div>
                                        </td>
                                        {dealers.map((d) => (
                                            <td key={d.id} className="ad2-c" title={a[d.id].why}>
                                                {a[d.id].level === 'none'
                                                    ? <span className="ad2-cell-none" aria-label={`None: ${a[d.id].why}`}>—</span>
                                                    : <AccessCell level={a[d.id].level} />}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="ad2-foot">
                    Expected access, derived from memberships, roles and data sources the way the server decides it. With
                    multi-dealership routing off, everyone who reaches the home dealership may also change it. Hover a cell for
                    the reason; click a row to change it at the source.
                </div>
            </div>
        </>
    );
}
AccessSection.propTypes = {
    c: PropTypes.object.isRequired, users: PropTypes.array.isRequired, tenants: PropTypes.array, onOpen: PropTypes.func.isRequired,
};
